/**
 * Security.
 *
 * The landing footer links here, and it is the page a cautious traveller looks
 * for before typing a diagnosis into a website. Every claim on it describes
 * something the system actually does — separate clinical storage, Keycloak
 * tokens, server-side entitlement, an access log on health records. Nothing
 * here should outrun the implementation; if a control changes, this changes.
 */

import { LEGAL } from "./legalDetails";
import { LegalList, LegalPageLayout, LegalSection } from "./LegalPageLayout";

export function SecurityPage() {
  const { contact } = LEGAL;

  return (
    <LegalPageLayout
      title="Security"
      subtitle="How your health data is kept, and what we do not do with it."
      description="HealthPass security: encryption in transit and at rest, separated clinical storage, Keycloak-managed sessions, server-side access control, audit logging and how to report a vulnerability."
      path="/security"
    >
      <LegalSection number={1} heading="Where your data lives">
        <p>
          Clinical records are stored apart from everything else. Health data
          sits in its own database in FHIR format; your identity, subscription
          and billing state sit in a different one; uploaded documents go to
          encrypted object storage. Nothing joins the two stores except code
          that has an explicit reason to. The servers are operated for us by
          Hetzner Online GmbH in Ashburn, Virginia, in the United States; the
          Privacy Policy explains what that means for data protection law and
          which safeguards the transfer rests on.
        </p>
      </LegalSection>

      <LegalSection number={2} heading="Encryption">
        <LegalList
          items={[
            "Everything between your device and us travels over TLS.",
            "Data is encrypted at rest, and uploaded documents are encrypted in storage.",
            "Backups are encrypted too — a backup is a copy of your health record and is treated as one.",
          ]}
        />
      </LegalSection>

      <LegalSection number={3} heading="Sign-in and sessions">
        <p>
          Authentication is handled by a dedicated identity server, which owns
          the whole token lifecycle; the application issues no tokens of its
          own. Access tokens are deliberately short-lived and are renewed in the
          background while you are using the app, so a stolen token is worth
          very little for very long. Passwords are never stored by the
          application in any form.
        </p>
      </LegalSection>

      <LegalSection number={4} heading="Who can read a record">
        <LegalList
          items={[
            "Every request for health data is authorised on the server. Hiding a button in the interface is not access control, so we do not rely on it — including for paid features.",
            "Reads of your health records are written to an audit log you can ask us for.",
            "Sharing is explicit: a healthcare provider, a companion or an emergency link sees what you granted, and you can revoke it.",
            "Emergency links are deliberately readable without a login — that is what makes them useful to a first responder — so they carry only the fields you put on the card, and you decide which.",
          ]}
        />
      </LegalSection>

      <LegalSection number={5} heading="Payments">
        <p>
          Card details go straight to our payment provider and never reach our
          servers. Incoming payment notifications are rejected unless their
          cryptographic signature checks out, and each one is processed exactly
          once.
        </p>
      </LegalSection>

      <LegalSection number={6} heading="What we deliberately do not do">
        <LegalList
          items={[
            "No advertising or analytics trackers, on any page. There is nothing to sell and nobody to sell it to.",
            "No health data in push notifications — the message says something is due, not what.",
            "No routine staff access to health records. Access for support is on your request, restricted and logged.",
          ]}
        />
      </LegalSection>

      <LegalSection number={7} heading="Offline copies on your device">
        <p>
          The app keeps a copy of your data on your device so it works without a
          connection — an emergency card that needs a network is not much of an
          emergency card. That copy lives in your browser&rsquo;s storage and is
          cleared when you sign out. On a shared device, sign out.
        </p>
      </LegalSection>

      <LegalSection number={8} heading="Reporting a vulnerability">
        <p>
          Write to{" "}
          <a
            className="text-primary-600 hover:underline"
            href={`mailto:${contact.general}`}
          >
            {contact.general}
          </a>{" "}
          with &ldquo;security&rdquo; in the subject. We answer within two
          working days. Please do not access, alter or retain other
          people&rsquo;s data while investigating, and give us a reasonable
          period to fix the problem before publishing it.
        </p>
      </LegalSection>

      <LegalSection number={9} heading="If something goes wrong">
        <p>
          No system is beyond reach. If a breach puts your rights at risk we
          notify the supervisory authority within 72 hours and tell you
          directly, as Articles 33 and 34 GDPR require.
        </p>
      </LegalSection>
    </LegalPageLayout>
  );
}

export default SecurityPage;
